import { FormInputRow, Input } from '@emerald-works-nova/components'
import { FormControl, FormHelperText, Grid, InputAdornment, InputLabel, MenuItem, Select, TextField } from '@mui/material'
import React from 'react'
import { Controller } from 'react-hook-form'
import AuthorSelector from './author-input'
import { ContentTypeRadioButton, ContentTypeSelect } from '../'
import { useRHFInitialValues } from '../../hooks'
import { useSelector } from 'react-redux'
import { AuthorSlice } from '../../reducers'
import { useEvent } from '@emerald-works/react-event-bus-client'
import { META_SKILL_OPTIONS } from '../../constants/meta-skill-options'

const ContentBasicInformationForm = ({
  control,
  errors = {},
  watch,
  setValue,
  initialValues = {},
  smallFormTitles = false,
  blockTypeOptions = false,
  isReadOnly,
  isEdit
}) => {
  useRHFInitialValues(initialValues, setValue)
  const authors = useSelector(AuthorSlice.selectors.selectAuthors)
  const authorsLoaded = useSelector(AuthorSlice.selectors.selectLoaded)
  const [getAuthors] = useEvent([AuthorSlice.eventBus.getAuthors])
  const isMSB = watch('primaryType')?.includes('msb')

  React.useEffect(() => {
    if (!authorsLoaded) {
      getAuthors.trigger()
    }
  }, [getAuthors, authorsLoaded])

  const authorValue = watch('author')
  const reviewerValue = watch('reviewer')

  const findAuthorId = React.useCallback((name) => {
    const matchedAuthor = authors.find(
      author => author.name.toLowerCase() === name.toLowerCase()
    )
    return matchedAuthor?.authorId ?? ''
  }, [authors])

  React.useEffect(() => {
    if (authorValue) {
      const authorId = findAuthorId(authorValue)
      return setValue('authorId', authorId)
    }
    return setValue('authorId', '')
  }, [authorValue, findAuthorId, setValue])

  React.useEffect(() => {
    if (reviewerValue) {
      const reviewerId = findAuthorId(reviewerValue)
      return setValue('reviewerId', reviewerId)
    }
    return setValue('reviewerId', '')
  }, [reviewerValue, findAuthorId, setValue])

  return (
    <Grid container>
      <Grid item xs={12}>
        <FormInputRow
          title='Title'
          smallFormTitles={smallFormTitles}
          description='This is the full name of the article or resource, as you want it to appear on screen. (Optimised for SEO)'
          InputComponent={
            <Input
              control={control}
              rules={{ required: true }}
              label='Title'
              name='title'
              valid={errors.title === undefined}
              error='Title is required'
              recommendedLength={48}
              maxLength={80}
              showErrors
              showCounter
              showHelperTextForCounter
              disabled={isReadOnly}
            />
          }
        />
      </Grid>
      <Grid item xs={12}>
        <FormInputRow
          title='Static Url'
          smallFormTitles={smallFormTitles}
          description='This information is automatically generated, but you can change it. You can edit the URL only upon the creation of the content, after saving it this field is blocked for edition. (Optimised for SEO)'
          InputComponent={
            <Input
              autoFocus
              control={control}
              rules={{ required: true }}
              label='Static Url'
              name='staticUrl'
              valid={errors.staticUrl === undefined}
              showHelperTextForCounter
              disabled={isReadOnly || isEdit}
            />
          }
        />
      </Grid>
      <Grid item xs={12}>
        <FormInputRow
          title='Subtitle'
          smallFormTitles={smallFormTitles}
          description='This is the subheading for articles and resources, optimised for SEO. Note: For Expert Interviews, Book Insights and video transcript pages, the Subtitle should the interviewee name; author(s) name and “Video Transcript” respectively. '
          InputComponent={
            <Input
              control={control}
              rules={{ required: true }}
              name='subtitle.html'
              valid={errors.subtitle === undefined}
              error='Subtitle is required'
              label='Add a Subtitle'
              multiline
              rows={4}
              recommendedLength={87}
              maxLength={105}
              showErrors
              showCounter
              showHelperTextForCounter
              disabled={isReadOnly}
            />
          }
        />
      </Grid>
      <Grid item xs={12}>
        <FormInputRow
          title='Meta title'
          smallFormTitles={smallFormTitles}
          description='This is the meta title for articles and resources, optimised for SEO. Note: Use the 60 character limit.'
          InputComponent={
            <Input
              control={control}
              rules={{ required: true }}
              name='metaTitle'
              valid={errors.metaTitle === undefined}
              error='Meta title is required'
              label='Add a Meta Title'
              recommendedLength={60}
              maxLength={60}
              showErrors
              showCounter
              showHelperTextForCounter
              disabled={isReadOnly}
            />
          }
        />
      </Grid>
      <Grid item xs={12}>
        <FormInputRow
          title='Meta Description'
          smallFormTitles={smallFormTitles}
          description='Descriptive text optimised for SEO. Compelling, active description, directly relevant to the content. Use the 155 character limit. '
          InputComponent={
            <Input
              control={control}
              rules={{ required: true }}
              name='metaDescription'
              valid={errors.metaDescription === undefined}
              error='Meta Description is required'
              label='Add a Meta Description'
              multiline
              rows={4}
              recommendedLength={255}
              maxLength={300}
              showErrors
              showCounter
              showHelperTextForCounter
              disabled={isReadOnly}
            />
          }
        />
      </Grid>
      <Grid item xs={12}>
        <FormInputRow
          title='Author'
          smallFormTitles={smallFormTitles}
          description='This is the content author'
          InputComponent={
            <Controller
              name='author'
              control={control}
              defaultValue={initialValues.author ?? ''}
              render={({ field: { onChange, value } }) => (
                <AuthorSelector
                  label='Add author name'
                  name='author'
                  value={value}
                  loading={getAuthors.isWorking}
                  disabled={isReadOnly}
                  authors={authors}
                  handleChange={(changed) => {
                    onChange(changed)
                  }}
                />
              )}
            />
          }
        />
      </Grid>
      <Grid item xs={12}>
        <FormInputRow
          title='Reviewer'
          smallFormTitles={smallFormTitles}
          description='This is the content reviewer'
          defaultValue={initialValues.reviewer ?? ''}
          InputComponent={
            <Controller
              name='reviewer'
              control={control}
              defaultValue={initialValues.author ?? ''}
              render={({ field: { onChange, value } }) => (
                <AuthorSelector
                  label='Add reviewer name'
                  name='reviewer'
                  value={value}
                  loading={getAuthors.isWorking}
                  disabled={isReadOnly}
                  authors={authors}
                  handleChange={(changed) => {
                    onChange(changed)
                  }}
                />
              )}
            />
          }
        />
      </Grid>
      <Grid item xs={12}>
        <FormInputRow
          title='Estimated Reading Time'
          smallFormTitles={smallFormTitles}
          description='Estimated reading time for the article or resource.'
          InputComponent={
            <Controller
              name='readTime'
              control={control}
              defaultValue={initialValues.readTime || 0}
              render={({ field: { onChange, value } }) => (
                <TextField
                  sx={{ width: '100%' }}
                  InputProps={{
                    endAdornment: <InputAdornment position='end'>Mins</InputAdornment>
                  }}
                  value={value}
                  onChange={e => onChange(Number(e.target.value))}
                  label='Add the Estimated Reading Time'
                  type='number'
                  disabled={isReadOnly}
                />
              )}
            />
          }
        />
      </Grid>
      <Grid item xs={12}>
        <FormInputRow
          title='Content Type'
          smallFormTitles={smallFormTitles}
          description='This is the type of the resource. E.g: Animated Video, How to Guides, Self-Assessments etc. '
          InputComponent={
            <Controller
              name='primaryType'
              control={control}
              rules={{ required: true }}
              defaultValue={initialValues.primaryType || ['article']}
              render={(
                { field: { onChange, value } }
              ) => (
                <ContentTypeSelect
                  onChange={onChange}
                  blockTypeOptions={blockTypeOptions}
                  value={value}
                />
              )}
            />
          }
        />
      </Grid>

      <Grid item xs={12}>
        <FormInputRow
          description='This is the format of the resource.'
          InputComponent={
            <Controller
              name='primaryType'
              control={control}
              rules={{ required: true }}
              defaultValue={(initialValues.primaryType && initialValues.primaryType[0]) || 'article'}
              render={({ field: { value } }) => <ContentTypeRadioButton blockTypeOptions value={value} />}
            />
          }
        />
      </Grid>

      {/* Conditional MSB fields */}
      {isMSB && (
        <>
          <Grid item xs={12}>
            <FormInputRow
              title='Meta Skill'
              smallFormTitles={smallFormTitles}
              description='Select the primary meta skill category for this MSB content.'
              InputComponent={
                <Controller
                  name='metaSkill'
                  control={control}
                  rules={{ required: isMSB }}
                  defaultValue={initialValues.metaSkill || ''}
                  render={({ field: { onChange, value } }) => (
                    <FormControl variant='outlined' fullWidth error={isMSB && errors.metaSkill !== undefined}>
                      <InputLabel id='meta-skill-select-label'>Select Meta Skill</InputLabel>
                      <Select
                        labelId='meta-skill-select-label'
                        id='meta-skill-select'
                        value={value}
                        onChange={(e) => {
                          onChange(e.target.value)
                          // Reset skill when meta skill changes
                          setValue('skill', '')
                        }}
                        label='Select Meta Skill'
                        disabled={isReadOnly}
                      >
                        {META_SKILL_OPTIONS.map((metaSkill) => (
                          <MenuItem key={metaSkill.value} value={metaSkill.value}>
                            {metaSkill.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {isMSB && errors.metaSkill && (
                        <FormHelperText>Meta Skill is required</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              }
            />
          </Grid>

          <Grid item xs={12}>
            <FormInputRow
              title='Skill'
              smallFormTitles={smallFormTitles}
              description='Select the specific skill within the chosen meta skill category.'
              InputComponent={
                <Controller
                  name='skill'
                  control={control}
                  rules={{ required: isMSB }}
                  defaultValue={initialValues.skill || ''}
                  render={({ field: { onChange, value } }) => {
                    const selectedMetaSkill = META_SKILL_OPTIONS.find(metaSkill => metaSkill.value === watch('metaSkill'))
                    const skillOptions = selectedMetaSkill ? selectedMetaSkill.skills : []
                    return (
                      <FormControl variant='outlined' fullWidth error={isMSB && errors.skill !== undefined}>
                        <InputLabel id='skill-select-label'>Select Skill</InputLabel>
                        <Select
                          labelId='skill-select-label'
                          id='skill-select'
                          value={value}
                          onChange={onChange}
                          label='Select Skill'
                          disabled={isReadOnly || !watch('metaSkill')}
                        >
                          {skillOptions.map((skill) => (
                            <MenuItem key={skill.value} value={skill.value}>
                              {skill.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {isMSB && errors.skill && (
                          <FormHelperText>Skill is required</FormHelperText>
                        )}
                        {!watch('metaSkill') && (
                          <FormHelperText>Please select a meta skill first</FormHelperText>
                        )}
                      </FormControl>
                    )
                  }}
                />
              }
            />
          </Grid>
        </>
      )}

    </Grid>
  )
}

export default ContentBasicInformationForm
